<script setup lang="ts">
import type { ThemeSettings } from '~/stores/auth'
import { hexToRgb } from '~/composables/useLayoutStyles'

interface DiscordServerData {
  code: string
  guildName: string
  guildIconUrl: string | null
  description: string | null
  memberCount: number
  onlineCount: number
  inviteUrl: string
}

const props = defineProps<{
  inviteCode: string
  themeSettings: ThemeSettings
}>()

const emit = defineEmits<{
  'load-failed': []
}>()

const serverData = ref<DiscordServerData | null>(null)
const loaded = ref(false)

async function fetchServer(code: string) {
  loaded.value = false
  serverData.value = null
  try {
    serverData.value = await $fetch<DiscordServerData>(`/api/discord/invite/${encodeURIComponent(code)}`)
  } catch {
    emit('load-failed')
  } finally {
    loaded.value = true
  }
}

onMounted(() => fetchServer(props.inviteCode))

watch(() => props.inviteCode, (code) => {
  if (code) fetchServer(code)
})

const cardBg = computed(() => {
  const rgb = hexToRgb(props.themeSettings.cardColor)
  return `rgba(${rgb.join(',')}, ${Math.min(props.themeSettings.cardOpacity / 100, 0.6)})`
})

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`
  return n.toLocaleString()
}
</script>

<template>
  <a
    v-if="loaded && serverData"
    :href="serverData.inviteUrl"
    target="_blank"
    rel="noopener noreferrer"
    class="block rounded-xl overflow-hidden transition-all duration-200 hover:scale-[1.02] group cursor-pointer no-underline"
    :style="{
      background: cardBg,
      border: '1px solid rgba(255, 255, 255, 0.08)'
    }"
  >
    <!-- Blurple accent bar -->
    <div class="h-1 w-full bg-[#5865F2]" />

    <!-- Content -->
    <div class="p-3">
      <!-- Icon + name row -->
      <div class="flex items-center gap-3">
        <img
          v-if="serverData.guildIconUrl"
          :src="serverData.guildIconUrl"
          :alt="serverData.guildName"
          class="size-10 rounded-lg object-cover shrink-0"
        >
        <div
          v-else
          class="size-10 rounded-lg bg-[#5865F2]/20 flex items-center justify-center shrink-0"
        >
          <UIcon
            name="i-simple-icons-discord"
            class="size-5 text-[#5865F2]"
          />
        </div>

        <div class="min-w-0 flex-1">
          <h4 class="text-sm font-semibold text-white truncate leading-tight">
            {{ serverData.guildName }}
          </h4>
          <p
            v-if="serverData.description"
            class="text-xs text-white/40 truncate leading-tight mt-0.5"
          >
            {{ serverData.description }}
          </p>
        </div>
      </div>

      <!-- Member counts + join -->
      <div class="flex items-center justify-between mt-2.5">
        <div class="flex items-center gap-3">
          <!-- Online -->
          <div class="flex items-center gap-1">
            <span class="relative flex size-2">
              <span class="relative inline-flex rounded-full size-2 bg-green-500" />
            </span>
            <span class="text-xs text-white/50">{{ formatCount(serverData.onlineCount) }} Online</span>
          </div>
          <!-- Total -->
          <div class="flex items-center gap-1">
            <span class="relative flex size-2">
              <span class="relative inline-flex rounded-full size-2 bg-white/25" />
            </span>
            <span class="text-xs text-white/50">{{ formatCount(serverData.memberCount) }} Members</span>
          </div>
        </div>

        <span class="text-xs font-medium text-[#5865F2] group-hover:text-[#7289DA] transition-colors flex items-center gap-1">
          Join
          <UIcon
            name="i-lucide-external-link"
            class="size-3"
          />
        </span>
      </div>

      <!-- Footer branding -->
      <div class="flex items-center justify-end mt-1.5">
        <UIcon
          name="i-simple-icons-discord"
          class="size-3 text-white/15 group-hover:text-[#5865F2] transition-colors"
        />
      </div>
    </div>
  </a>
</template>
